import {
  addAdmin,
  deleteAdmin,
  getAdmins,
  updateAdmin,
} from "../actions/admin.action";
import TableList from "../components/TableList";
import { AdminPermission, checkIfHasPermission } from "../models/auth.model";
import { CheckCircleOutline, HighlightOff } from "@mui/icons-material";
import { green, red } from "@mui/material/colors";
import { Chip } from "@mui/material";
import { useRef } from "react";
import AdminForm from "./AdminForm";
import { filterOnKeys, titleStr } from "../lib/helper";
import { FilterGroup, FilterType } from "../components/TableFilter";
import { FormRefEvent, HeadCell } from "../models/base.model";
import { AdminRole, Admin } from "../models/admin.model";

const headCells: readonly HeadCell[] = [
  {
    id: "username",
    label: "User Name",
  },
  {
    id: "name",
    label: "Name",
  },
  {
    id: "title",
    label: "Title",
  },
  {
    id: "role",
    label: "Role",
    render: (value?: AdminRole) => (
      <Chip label={titleStr(value?.name)} color="info" />
    ),
  },
  {
    id: "email",
    label: "Email",
  },
  {
    id: "enabled",
    label: "Enabled",
    render: (value) =>
      value ? (
        <CheckCircleOutline sx={{ color: green[500] }} />
      ) : (
        <HighlightOff sx={{ color: red[500] }} />
      ),
  },
];

const filters: FilterGroup<Admin>[] = [
  {
    title: "Filter By Date",
    items: [
      {
        name: "createdAt",
        type: FilterType.date_range,
      },
      {
        name: "updatedAt",
        type: FilterType.date_range,
      },
    ],
  },
  {
    title: "Filter By Status",
    items: [
      {
        name: "enabled",
        type: FilterType.selection,
        options: [
          { value: true, label: "Enabled" },
          { value: false, label: "Disabled" },
        ],
      },
    ],
  },
];

export default function Admins() {
  const formRef = useRef<FormRefEvent<Admin>>(null);

  const onAddOrUpdate = (param?: Admin) => {
    return window.webApp.setTab<Admin>({
      title: `${param ? "Update" : "New"} Admin`,
      element: <AdminForm ref={formRef} defaultValues={param} />,
      onOk: async () => {
        if (!formRef.current) {
          throw new Error("Form creation error, please refresh or contact us!");
        }
        const value = await formRef.current.onSubmit();
        const res = param
          ? await updateAdmin({ ...param, ...value })
          : await addAdmin(value);
        return res;
      },
      show: true,
    });
  };

  const onDelete = (param: Admin) => {
    return deleteAdmin(param.username);
  };

  const onSearch = async (value: string, original: Admin[]) => {
    if (original) {
      return {
        data: original.filter((a) =>
          filterOnKeys(a, ["username", "name"], value),
        ),
      };
    } else {
      return { data: [] };
    }
  };
  const onEditAction = checkIfHasPermission([AdminPermission.admin_write])
    ? onAddOrUpdate
    : undefined;
  const onDeleteAction = checkIfHasPermission([AdminPermission.admin_delete])
    ? onDelete
    : undefined;
  return (
    <TableList<Admin>
      headCells={headCells}
      onAddBtnText="New Admin"
      uid="username"
      onAdd={onEditAction}
      onSearch={onSearch}
      searchText="Search by username or name"
      onEdit={onEditAction}
      onDelete={onDeleteAction}
      noAction={!onEditAction && !onDeleteAction}
      onLoad={getAdmins}
      filters={filters}
    />
  );
}
